import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faArrowLeft,
  faCheckCircle,
  faCopy,
  faDownload,
  faHistory,
  faKey,
  faRefresh,
  faSearch,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import {
  exportMemberEmployeeIdBatch,
  generateMemberEmployeeIds,
  getMemberEmployeeIdGenerationHistory,
} from "../../services/memberEmployeeIdGeneratorApi";

const MAX_GENERATE_COUNT = 5000;
const HISTORY_LIMIT = 10;

const escapeCsvValue = (value) => {
  const normalized = String(value ?? "");
  if (/[",\r\n]/.test(normalized)) {
    return `"${normalized.replace(/"/g, '""')}"`;
  }
  return normalized;
};

const downloadEmployeeIdsCsv = (employeeIds, fileName) => {
  const rows = ["employee_id", ...employeeIds.map(escapeCsvValue)];
  const blob = new Blob([rows.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", fileName);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
  URL.revokeObjectURL(url);
};

const getFilenameFromDisposition = (contentDisposition, fallbackFileName) => {
  if (!contentDisposition) return fallbackFileName;
  const match = contentDisposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallbackFileName;
};

const formatDateTime = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-PH", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const EmployeeIdGeneratorPage = () => {
  const navigate = useNavigate();
  const [count, setCount] = useState(10);
  const [generating, setGenerating] = useState(false);
  const [generationResult, setGenerationResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [exportingBatchId, setExportingBatchId] = useState(null);

  const generatedEmployeeIds = useMemo(
    () => generationResult?.employee_ids || [],
    [generationResult]
  );

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const response = await getMemberEmployeeIdGenerationHistory({
        page,
        limit: HISTORY_LIMIT,
        search: appliedSearch,
      });
      setHistory(response.data.data || []);
      setPagination(response.data.pagination || {});
    } catch (error) {
      const message = error.response?.data?.message || "Failed to fetch generation history.";
      toast.error(typeof message === "string" ? message : "Failed to fetch generation history.");
    } finally {
      setHistoryLoading(false);
    }
  }, [page, appliedSearch]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleGenerate = async () => {
    const requestedCount = Number.parseInt(count, 10);
    if (!Number.isInteger(requestedCount) || requestedCount < 1) {
      toast.error("Enter a valid count.");
      return;
    }
    if (requestedCount > MAX_GENERATE_COUNT) {
      toast.error(`Maximum count is ${MAX_GENERATE_COUNT}.`);
      return;
    }

    setGenerating(true);
    try {
      const response = await generateMemberEmployeeIds(requestedCount);
      const data = response.data.data || {};
      setGenerationResult(data);
      toast.success(response.data.message || "Employee IDs generated.");
      setPage(1);
      setAppliedSearch("");
      setSearch("");
      await fetchHistory();
    } catch (error) {
      const message = error.response?.data?.message || "Failed to generate employee IDs.";
      toast.error(typeof message === "string" ? message : "Failed to generate employee IDs.");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadCurrent = () => {
    if (generatedEmployeeIds.length === 0 || !generationResult?.batch_id) {
      toast.warning("No generated employee IDs to download.");
      return;
    }

    downloadEmployeeIdsCsv(
      generatedEmployeeIds,
      `member_employee_ids_${generationResult.batch_id.slice(0, 8)}.csv`
    );
    toast.success("CSV downloaded.");
  };

  const handleCopyCurrent = async () => {
    if (generatedEmployeeIds.length === 0) {
      toast.warning("No generated employee IDs to copy.");
      return;
    }

    try {
      await navigator.clipboard.writeText(generatedEmployeeIds.join("\n"));
      toast.success("Employee IDs copied.");
    } catch (error) {
      toast.error("Copy failed.");
    }
  };

  const handleExportBatch = async (batchId) => {
    setExportingBatchId(batchId);
    try {
      const response = await exportMemberEmployeeIdBatch(batchId);
      const fallbackFileName = `member_employee_ids_${batchId.slice(0, 8)}.csv`;
      const fileName = getFilenameFromDisposition(
        response.headers?.["content-disposition"],
        fallbackFileName
      );
      const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success("Batch CSV exported.");
    } catch (error) {
      const message = error.response?.data?.message || "Export failed.";
      toast.error(typeof message === "string" ? message : "Export failed.");
    } finally {
      setExportingBatchId(null);
    }
  };

  const handleSearch = () => {
    setAppliedSearch(search.trim());
    setPage(1);
  };

  const handleClearSearch = () => {
    setSearch("");
    setAppliedSearch("");
    setPage(1);
  };

  const totalPages = Math.max(1, Number(pagination.last_page || 1));

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Employee ID Generator</h1>
          <p className="mt-1 text-sm text-gray-600">
            Generate reserved 7-character employee IDs for member enrollment.
          </p>
        </div>
        <button
          onClick={() => navigate("/members")}
          className="inline-flex items-center gap-2 rounded border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
        >
          <FontAwesomeIcon icon={faArrowLeft} />
          Back to Members
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[360px_1fr]">
        <section className="rounded-lg bg-white p-5 shadow-sm">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded bg-blue-50 text-blue-700">
              <FontAwesomeIcon icon={faKey} />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Generate IDs</h2>
              <p className="text-xs text-gray-500">
                7 uppercase alphanumeric characters. Maximum {MAX_GENERATE_COUNT} per request
              </p>
            </div>
          </div>

          <label className="mb-1 block text-xs font-semibold uppercase text-gray-600">
            Number of employee IDs
          </label>
          <input
            type="number"
            min="1"
            max={MAX_GENERATE_COUNT}
            value={count}
            onChange={(event) => setCount(event.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          />

          <div className="mt-3 flex flex-wrap gap-2">
            {[10, 50, 100, 500].map((preset) => (
              <button
                key={preset}
                onClick={() => setCount(preset)}
                className="rounded border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
              >
                {preset}
              </button>
            ))}
          </div>

          <button
            onClick={handleGenerate}
            disabled={generating}
            className="mt-5 flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-300"
          >
            <FontAwesomeIcon icon={faKey} className={generating ? "animate-pulse" : ""} />
            {generating ? "Generating..." : "Generate Employee IDs"}
          </button>
        </section>

        <section className="rounded-lg bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Generated Employee IDs</h2>
              <p className="text-xs text-gray-500">
                {generationResult?.batch_id ? `Batch ${generationResult.batch_id}` : "No active batch"}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleCopyCurrent}
                disabled={generatedEmployeeIds.length === 0}
                className="inline-flex items-center gap-2 rounded border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faCopy} />
                Copy
              </button>
              <button
                onClick={handleDownloadCurrent}
                disabled={generatedEmployeeIds.length === 0}
                className="inline-flex items-center gap-2 rounded bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-green-300"
              >
                <FontAwesomeIcon icon={faDownload} />
                Download CSV
              </button>
            </div>
          </div>

          {generatedEmployeeIds.length > 0 ? (
            <>
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs font-semibold uppercase text-gray-500">Count</div>
                  <div className="mt-1 text-xl font-bold text-gray-800">{generatedEmployeeIds.length}</div>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs font-semibold uppercase text-gray-500">Generated By</div>
                  <div className="mt-1 truncate text-sm font-semibold text-gray-800">
                    {generationResult.generated_by || "-"}
                  </div>
                </div>
                <div className="rounded border border-gray-200 bg-gray-50 p-3">
                  <div className="text-xs font-semibold uppercase text-gray-500">Generated At</div>
                  <div className="mt-1 text-sm font-semibold text-gray-800">
                    {formatDateTime(generationResult.generated_at)}
                  </div>
                </div>
              </div>

              <div className="max-h-[420px] overflow-auto rounded border border-gray-200">
                <div className="grid grid-cols-1 gap-px bg-gray-200 sm:grid-cols-2 lg:grid-cols-3">
                  {generatedEmployeeIds.map((employeeId) => (
                    <div
                      key={employeeId}
                      className="bg-white px-3 py-2 font-mono text-xs font-semibold text-gray-800"
                    >
                      {employeeId}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="flex min-h-[260px] items-center justify-center rounded border border-dashed border-gray-300 text-sm text-gray-500">
              No generated IDs yet.
            </div>
          )}
        </section>
      </div>

      <section className="mt-6 rounded-lg bg-white shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faHistory} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-800">Generation History</h2>
          </div>
          <button
            onClick={fetchHistory}
            disabled={historyLoading}
            className="inline-flex items-center gap-2 rounded bg-gray-200 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-300 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRefresh} className={historyLoading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        <div className="border-b border-gray-200 px-5 py-4">
          <div className="flex flex-col gap-3 md:flex-row">
            <div className="flex-1">
              <input
                type="text"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && handleSearch()}
                placeholder="Search batch, employee ID, or username"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleSearch}
                className="inline-flex items-center gap-2 rounded bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
              >
                <FontAwesomeIcon icon={faSearch} />
                Search
              </button>
              {appliedSearch && (
                <button
                  onClick={handleClearSearch}
                  className="inline-flex items-center gap-2 rounded bg-gray-200 px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-300 bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Batch ID</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Count</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Generated By</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Generated At</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700">Action</th>
              </tr>
            </thead>
            <tbody>
              {historyLoading ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : history.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                    No generation history found.
                  </td>
                </tr>
              ) : (
                history.map((item) => (
                  <tr key={item.generation_batch_id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-800">
                      {item.generation_batch_id}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex items-center gap-1 rounded bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                        <FontAwesomeIcon icon={faCheckCircle} />
                        {item.total_generated}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{item.generated_by_name || "-"}</td>
                    <td className="px-4 py-3 text-gray-700">{formatDateTime(item.generated_at)}</td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleExportBatch(item.generation_batch_id)}
                        disabled={exportingBatchId === item.generation_batch_id}
                        className="inline-flex items-center gap-2 rounded bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:bg-green-300"
                      >
                        <FontAwesomeIcon icon={faDownload} />
                        {exportingBatchId === item.generation_batch_id ? "Exporting..." : "CSV"}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <div className="text-xs text-gray-500">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1 || historyLoading}
              className="inline-flex items-center gap-2 rounded border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faAngleLeft} />
              Previous
            </button>
            <button
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page >= totalPages || historyLoading}
              className="inline-flex items-center gap-2 rounded border border-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
              <FontAwesomeIcon icon={faAngleRight} />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EmployeeIdGeneratorPage;
